<template>
  <TransactionAssetField
    v-model="modelValue"
    v-bind="{
      ...$attrs,
      data,
      error,
      description,
      settings,
    }"
    @create="handleCreate"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeMount, provide } from 'vue'

import { Asset, TransactionClass, TransactionComputedEntry } from '@types'

import { usePowerMode } from '@/hooks/powerMode/powerMode'

import { getAssetListValues, onCreateAsset, preSetAsset } from './utils/helpers'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useRepositoriesStore } from '@/store/repositories/index'
import { useModalsStore } from '@/store/modals'

import TransactionAssetField from './TransactionAssetField.vue'

type Props = {
  entryIndex: number
  instance: TransactionClass
  assetsList?: Asset[]
}

const props = defineProps<Props>()

const { isPowerMode, clickOnPowerMode } = usePowerMode()

const assetsBunchStore = useAssetsBunchStore()
const repositoriesStore = useRepositoriesStore()
const modalsStore = useModalsStore()

const entryKey = computed(() => `entry${props.entryIndex + 1}` as keyof TransactionClass)

const modelValue = computed({
  get() {
    return (props.instance[entryKey.value] as TransactionComputedEntry)().value?.account_id || undefined
  },
  set(value) {
    (props.instance[entryKey.value] as TransactionComputedEntry)().value = {
      ...(props.instance[entryKey.value] as TransactionComputedEntry)().value,
      account_id: value || null
    }
  },
})

const entrySettings = computed(
  () => props.instance.settings?.entries[props.entryIndex],
)
const excludeLinkedAccounts = computed(() => !!props.instance.settings?.excludeLinkedAccounts)
const settings = computed(() => entrySettings.value?.account)

const error = computed(
  () => props.instance.errors[`"${entrySettings.value?.title}" account`],
)

const description = computed(() => entrySettings.value?.accountDescription)

const accountsList = computed(() => {
  if (props.assetsList) return props.assetsList
  return Array.from(assetsBunchStore.getList.values())
    .map(instance => instance.get())
})

const currencyID = computed(() => {
  const asset =
    assetsBunchStore.getElementByName(repositoriesStore.getCurrentCurrency) ||
    assetsBunchStore.getElementByTicker(repositoriesStore.getCurrentCurrency)
  return asset?.id
})

const data = computed(() =>
  getAssetListValues(
    settings.value,
    accountsList.value,
    (props.instance[entryKey.value] as TransactionComputedEntry)().value?.asset_id,
    excludeLinkedAccounts.value && !isPowerMode?.value,
  ),
)

const handleCreate = (value: string) => {
  const instance = assetsBunchStore.createElement()
  onCreateAsset(
    value,
    settings.value?.includes,
    instance,
    modalsStore,
    (data: string) => {
      modelValue.value = data
    },
  )
}

const selectionChecker = (_: any, disabled?: boolean) => {
  if (disabled && !isPowerMode?.value) {
    return clickOnPowerMode()
  }
  return Promise.resolve()
}
provide('selectionChecker', selectionChecker)

onBeforeMount(() => {
  if (modelValue.value) return
  modelValue.value =
    preSetAsset(
      modelValue.value,
      settings.value,
      assetsBunchStore,
      currencyID.value,
    ) || ''
})
</script>
